import React, { useEffect, useMemo, useState } from 'react';
import Autosuggest, { ChangeEvent, SuggestionSelectedEventData } from 'react-autosuggest';
import _debounce from 'lodash/debounce';
import cx from 'classnames';

// Types
import { FieldInputProps, FieldMetaState } from 'react-final-form';
import type { WrappedFieldInputProps, WrappedFieldMetaProps } from 'redux-form';

// Utils
import { MappedVendorSearchResult, vendorMarketplaceSearch } from 'api/vendorMarketplaceApi';

// Styles
import sharedStyles from 'components/onboard/questions/WeddingLocationQuestion/WeddingLocationQuestion.module.less';
import {
  autosuggestVenueFieldContainer,
  suggestionContainer,
  SuggestionTextMain,
  SuggestionTextSecondary,
  ShowMoreContainer,
  StyledArrowIcon,
  StyledLinkV2,
} from './AutosuggestVenueField.styles';

/**
 * Type accounts for either a vendor marketplace or google suggestion.
 * Label, placeId, isFirst (all opt) are specific to google suggestions.
 */
export interface MappedVendorOrGoogleSearch extends MappedVendorSearchResult {
  label?: string;
  placeId?: string;
  isFirst?: boolean;
}

type AutosuggestVenueFieldProps = {
  /** className */
  className?: string;
  /** Input field identifier */
  id: string;
  /** Input field props from either react-final-form | redux-form */
  input: FieldInputProps<string, HTMLElement> | WrappedFieldInputProps;
  /** Input field label */
  label?: string | React.ReactNode;
  /** Max character count allowed */
  maxChars?: number;
  /** Meta data holding error message from either react-final-form | redux-form */
  meta: FieldMetaState<string> | WrappedFieldMetaProps;
  /** Callback for onblur event */
  onInputBlur?: (e: React.FormEvent<HTMLElement>, newValue?: string) => void;
  /** Callback for when reset button is clicked */
  onResetButtonClick: () => void;
  /** Callback for when a suggestion is selected */
  onSuggestSelect: (suggestion: MappedVendorOrGoogleSearch) => void;
  /** Placeholder text for input */
  placeholder?: string;
  /** Option to display or hide reset button which defaults to true */
  showResetButton?: boolean;
  /** Option to provide Google Place suggestions */
  useGoogleSuggestions?: boolean;
};

/**
 * Field that renders both vendor marketplace and google autosuggestions.
 * Acts as an adapter component that works for either react-final-form or redux-form.
 * Also allows for manual entry of venue name if autosuggestion is not selected.
 */
const AutosuggestVenueField: React.FC<AutosuggestVenueFieldProps> = ({
  className,
  id,
  input,
  label,
  maxChars,
  meta: { error, touched },
  onInputBlur,
  onSuggestSelect,
  onResetButtonClick,
  placeholder,
  showResetButton = true,
  useGoogleSuggestions = true,
}) => {
  // Need this to keep dropdown open when show more button is selected with keypress event
  // since shouldKeepSuggestionsOnSelect only seems to work for click event
  // should be false by default, but setting to true if is Jest test to help with dropdown rendering
  const [alwaysRenderSuggestions, setAlwaysRenderSuggestions] = useState(
    process.env.JEST_WORKER_ID !== undefined
  );
  // Need this to set as input value when keying down to show more button
  const [originallyTypedVenueName, setOriginallyTypedVenueName] = useState<string>(input.value);
  // Main value that stays up to date on user input
  const [venueName, setVenueName] = useState<string>(input.value);
  // Venue search results that populates the autosuggestion dropdown
  const [venueNameSuggestions, setVenueNameSuggestions] = useState<MappedVendorOrGoogleSearch[]>(
    []
  );
  // Used to hold google suggestions before hitting show more button
  const [googleSuggestions, setGoogleSuggestions] = useState<MappedVendorOrGoogleSearch[]>([]);
  // Hides google suggestions until user hits show more button within dropdown
  const [showGoogleSuggestions, setShowGoogleSuggestions] = useState(false);
  // For error handling
  const errorMessage = touched && error;

  // Setup for google maps API
  const [
    googleAutocompleteService,
    setGoogleAutocompleteService,
  ] = useState<google.maps.places.AutocompleteService | null>(null);
  const [googleSessionToken, setGoogleSessionToken] = useState<
    google.maps.places.AutocompleteSessionToken
  >();
  useEffect(() => {
    if (typeof window === 'undefined' || !useGoogleSuggestions) {
      return;
    }
    const googleMaps = window.google && window.google.maps;
    setGoogleAutocompleteService(new googleMaps.places.AutocompleteService());
    setGoogleSessionToken(new googleMaps.places.AutocompleteSessionToken());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (input.value !== venueName) {
      setVenueName(input.value);
      setOriginallyTypedVenueName(input.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input.value]);

  // Helper function to determine if item in dropdown is show more button and not a suggestion
  const isShowMoreButton = (valueToCompare?: string): boolean => valueToCompare === 'Show more';

  // Value that populates input as you tab through suggestions or once you click one
  const getSuggestionValue = (suggestion: MappedVendorOrGoogleSearch): string => {
    if (isShowMoreButton(suggestion.label)) {
      setAlwaysRenderSuggestions(true);
      return originallyTypedVenueName;
    }
    setAlwaysRenderSuggestions(false);
    return suggestion.label || suggestion.name;
  };

  // Clears all suggestions in the autosuggest dropdown
  const clearSuggestions = (): void => {
    setVenueNameSuggestions([]);
    setGoogleSuggestions([]);
    setShowGoogleSuggestions(false);
  };

  // Invoked by the form control when the value changes
  const onChange = (e: React.FormEvent<HTMLElement>, params: ChangeEvent): void => {
    const { newValue } = params;
    setVenueName(newValue);
    input.onChange(newValue);
  };

  // Handler for showing google items when show more is clicked
  const onShowGoogleSuggestions = (): void => {
    setAlwaysRenderSuggestions(false);
    setShowGoogleSuggestions(true);
    setVenueNameSuggestions(originalSuggestions => [
      ...originalSuggestions.slice(0, originalSuggestions.length - 1),
      ...googleSuggestions,
    ]);
    setGoogleSuggestions([]);
  };

  // Fetches vendor marketplace suggestions
  const fetchMarketplaceSuggestions = async (
    inputVal: string
  ): Promise<MappedVendorOrGoogleSearch[]> => {
    return vendorMarketplaceSearch(inputVal)
      .then(marketplaceSuggestions => {
        return marketplaceSuggestions;
      })
      .catch(() => {
        throw new Error('Vendor marketplace suggestions not fetched');
      });
  };

  // Fetches google suggestions
  const fetchGoogleSuggestions = useMemo(
    () => async (inputVal: string): Promise<MappedVendorOrGoogleSearch[]> => {
      const options: google.maps.places.AutocompletionRequest = {
        input: inputVal,
        sessionToken: googleSessionToken,
      };
      return (
        googleAutocompleteService
          ?.getPlacePredictions(options)
          .then(suggestions => {
            const mappedSuggestions = suggestions?.predictions.map((prediction, i) => {
              return ({
                label: prediction.description,
                placeId: prediction.place_id,
                isFirst: i === 0,
              } as unknown) as MappedVendorSearchResult;
            });
            setGoogleSuggestions(mappedSuggestions);
            return mappedSuggestions;
          })
          .catch(() => {
            throw new Error('Google suggestions not fetched');
          }) || []
      );
    },
    [googleAutocompleteService, googleSessionToken]
  );

  // Handler for when input value has changed and we need to fetch new suggestions
  const onSuggestionsFetchRequested = useMemo(
    () =>
      _debounce(async ({ value }) => {
        const shouldNotFetchNew = value === originallyTypedVenueName;
        const isInputFieldEmpty = value.trim().length === 0;
        if (shouldNotFetchNew || isInputFieldEmpty) {
          if (isInputFieldEmpty) clearSuggestions();
          return;
        }
        setOriginallyTypedVenueName(value);
        setShowGoogleSuggestions(false);
        const suggestions = await fetchMarketplaceSuggestions(value);
        const gSuggestions = await fetchGoogleSuggestions(value);
        if (gSuggestions.length > 0) {
          const showMore = { label: 'Show more' } as MappedVendorOrGoogleSearch;
          suggestions.push(showMore);
        }
        setVenueNameSuggestions(suggestions);
      }, 500),
    [fetchGoogleSuggestions, originallyTypedVenueName]
  );

  // Keeps dropdown open when show more button is clicked on
  const shouldKeepSuggestionsOnSelect = (suggestion: MappedVendorOrGoogleSearch): boolean =>
    isShowMoreButton(suggestion.label);

  // Handles suggestion selection for either marketplace or google item
  const onSuggestionSelected = (
    _event: React.FormEvent<HTMLElement>,
    params: SuggestionSelectedEventData<MappedVendorOrGoogleSearch>
  ) => {
    const { suggestion } = params;
    if (isShowMoreButton(suggestion.label)) {
      onShowGoogleSuggestions();
      return;
    }
    onSuggestSelect(suggestion);
  };

  // Formats secondary location text for a marketplace suggestion
  const formatLocation = (suggestion: MappedVendorOrGoogleSearch): string => {
    const { address } = suggestion;
    const location = [address.city, address.stateProvince];
    return location.filter(item => item).join(', ');
  };

  // Renders each suggestion or show more button in the autosuggest dropdown
  const renderSuggestion = (suggestion: MappedVendorOrGoogleSearch): JSX.Element => {
    const vendorSuggestion = suggestion.name;
    const googleSuggestion = suggestion.label;
    const isFirstGoogleSuggestion = googleSuggestion && suggestion.isFirst;

    if (isShowMoreButton(googleSuggestion)) {
      return (
        <ShowMoreContainer>
          Show more <StyledArrowIcon width={10} height={8} />
        </ShowMoreContainer>
      );
    }

    return (
      <div
        css={suggestionContainer}
        className={cx({ 'react-autosuggest__suggestion--first-google': isFirstGoogleSuggestion })}
      >
        <SuggestionTextMain>{googleSuggestion || vendorSuggestion}</SuggestionTextMain>
        {vendorSuggestion && (
          <SuggestionTextSecondary>{formatLocation(suggestion)}</SuggestionTextSecondary>
        )}
      </div>
    );
  };

  // Handles reset button click
  const onClickResetButton = () => {
    if (onResetButtonClick) {
      onResetButtonClick();
    }
  };

  return (
    <div
      className={cx(
        sharedStyles.autoSuggest,
        'zola-ui render-field',
        {
          'has-error': errorMessage,
        },
        {
          'with-google-suggestions': showGoogleSuggestions,
        },
        className
      )}
      css={autosuggestVenueFieldContainer}
    >
      {label ? <label htmlFor={id}>{label}</label> : <></>}
      {showResetButton ? (
        <StyledLinkV2 arrow inline onClick={onClickResetButton} role="button">
          Reset address
        </StyledLinkV2>
      ) : (
        <></>
      )}
      <Autosuggest<MappedVendorOrGoogleSearch>
        alwaysRenderSuggestions={alwaysRenderSuggestions}
        inputProps={{
          ...input,
          className: 'form-control',
          id,
          onBlur: onInputBlur
            ? (e: React.FormEvent<HTMLElement>) => onInputBlur(e, venueName)
            : undefined,
          onChange,
          placeholder,
          value: venueName,
        }}
        getSuggestionValue={getSuggestionValue}
        onSuggestionsClearRequested={clearSuggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionSelected={onSuggestionSelected}
        renderSuggestion={renderSuggestion}
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        shouldKeepSuggestionsOnSelect={shouldKeepSuggestionsOnSelect}
        suggestions={venueNameSuggestions}
      />
      {errorMessage ? (
        <div className="secondary-help-block max-characters-block">
          <span className="text-danger">{errorMessage}</span>
          <span className="text-danger">
            {venueName.length}/{maxChars}
          </span>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

export default AutosuggestVenueField;
